import { Controller } from "stimulus";
// John Hancock will be loaded via script tag in the view

export default class extends Controller {
  static targets = ["modal", "canvas", "nameInput", "locationModal"];

  connect() {
    this.blockId = this.element.dataset.blockId;
    this.readOnly = this.element.dataset.signatureBlockReadOnly === "true";
    this.allowRecreation = this.element.dataset.signatureBlockAllowRecreation === "true";
    this.signatureData = this.element.dataset.signatureBlockSignatureData;
    this.signedAt = this.element.dataset.signatureBlockSignedAt;
    this.signedByName = this.element.dataset.signatureBlockSignedByName;
    
    // Parse signature metadata
    try {
      const metadataStr = this.element.dataset.signatureBlockMetadata;
      this.signatureMetadata = metadataStr ? JSON.parse(metadataStr) : null;
    } catch (e) {
      console.warn('Failed to parse signature metadata:', e);
      this.signatureMetadata = null;
    }
    
    // Get browser's actual timezone (most reliable)
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Get user's timezone from database
    const userTimezoneFromDB = this.element.dataset.userTimezone;
    
    // Use browser timezone if user timezone is UTC, empty, or not set
    // This ensures we use the actual location timezone even if user profile isn't set correctly
    if (!userTimezoneFromDB || userTimezoneFromDB.trim() === '' || userTimezoneFromDB === 'UTC') {
      this.userTimezone = browserTimezone;
      console.log('Using browser timezone (user timezone is UTC/empty):', browserTimezone);
    } else {
      this.userTimezone = userTimezoneFromDB;
      console.log('Using user timezone from database:', userTimezoneFromDB);
    }
    
    // Debug: Log timezone information
    console.log('Signature Block Controller - Final Timezone:', this.userTimezone);
    console.log('Signature Block Controller - Browser Timezone:', browserTimezone);
    console.log('Signature Block Controller - User Timezone from DB:', userTimezoneFromDB);
    
    this.element.signatureBlockController = this;
    
    // Update any server-rendered timestamps to use browser timezone
    this.updateServerRenderedTimestamps();
    
    this.ensureAllModalsHidden();
    
    this.setupConfigModeListeners();
    
    this.setupFormSubmitListener();
    
    // Initialize any existing maps on this element
    setTimeout(() => {
      this.reinitializeAllMaps();
    }, 200);
    
    setTimeout(() => {
      this.updateRecreateButtonVisibility();
    }, 100);
  }
  
  updateServerRenderedTimestamps() {
    // Find all signature date elements with UTC time data attribute
    const dateElements = this.element.querySelectorAll('.signature-date[data-utc-time]');
    dateElements.forEach(element => {
      const utcTimeString = element.getAttribute('data-utc-time');
      if (utcTimeString) {
        try {
          const utcDate = new Date(utcTimeString);
          const formatted = this.formatDateForUserTimezone(utcDate);
          element.textContent = formatted;
          console.log('Updated server-rendered timestamp:', formatted);
        } catch (e) {
          console.warn('Failed to update server-rendered timestamp:', e);
        }
      }
    });
  }

  setupFormSubmitListener() {
    const form = this.element.closest('form');
    if (!form) return;
    
    if (form.dataset.signatureFormListenerAdded) return;
    form.dataset.signatureFormListenerAdded = 'true';
    
    const self = this;
    form.addEventListener('submit', function(e) {
      const blockId = self.blockId;
      if (!blockId) return;
      
      const nameInputField = document.getElementById(`signature-name-input-${blockId}`);
      if (!nameInputField) return;
      
      const nameValue = nameInputField.value || '';
      
      const allIdFields = form.querySelectorAll('input[name*="task_blocks_attributes"][name*="[id]"]');
      allIdFields.forEach((field) => {
        if (field.value === String(blockId) || field.value === blockId) {
          const nameMatch = field.name.match(/task_blocks_attributes\[(\d+)\]/);
          if (nameMatch) {
            const index = nameMatch[1];
            const nestedField = form.querySelector(`input[name="task[task_blocks_attributes][${index}][signed_by_name]"]`);
            if (nestedField) {
              nestedField.value = nameValue;
            }
          }
        }
      });
      
      const directField = form.querySelector('input[name="task_block[signed_by_name]"]');
      if (directField) {
        directField.value = nameValue;
      }
    });
  }

  ensureAllModalsHidden() {
    const allConfigModals = document.querySelectorAll('.signature-modal-config');
    const allDisplayModals = document.querySelectorAll('.signature-modal');
    
    [...allConfigModals, ...allDisplayModals].forEach(modal => {
      modal.classList.add("hidden");
      modal.style.setProperty('display', 'none', 'important');
      modal.style.setProperty('visibility', 'hidden', 'important');
      modal.style.setProperty('opacity', '0', 'important');
    });
  }
  
  disconnect() {
    if (this.element.signatureBlockController === this) {
      delete this.element.signatureBlockController;
    }
  }

  setupConfigModeListeners() {
    if (window.signatureBlockConfigListenersSetup) {
      return;
    }
    window.signatureBlockConfigListenersSetup = true;
    
    const self = this;
    document.addEventListener('click', function(e) {
      const drawButton = e.target.closest('.signature-draw-button-config');
      if (drawButton && (e.target === drawButton || drawButton.contains(e.target))) {
        e.preventDefault();
        e.stopPropagation();
        const blockId = drawButton.dataset.blockId || 'new';
        self.openSignatureModalConfig(blockId);
        return;
      }
      
      const updateButton = e.target.closest('.signature-update-button-config');
      if (updateButton && (e.target === updateButton || updateButton.contains(e.target))) {
        e.preventDefault();
        e.stopPropagation();
        let blockId = updateButton.dataset.blockId || 'new';
        
        if (!blockId || blockId === 'new') {
          const form = updateButton.closest('form');
          if (form) {
            const idFields = form.querySelectorAll('input[name*="task_blocks_attributes"][name*="[id]"]');
            for (let field of idFields) {
              const blockTypeField = form.querySelector(`input[name="${field.name.replace('[id]', '[block_type]')}"]`);
              if (blockTypeField && blockTypeField.value === 'signature' && field.value) {
                blockId = field.value;
                break;
              }
            }
          }
        }
        
        self.openSignatureModalConfig(blockId);
        return;
      }
      
      const closeButton = e.target.closest('.signature-modal-close-config');
      if (closeButton && (e.target === closeButton || closeButton.contains(e.target))) {
        e.preventDefault();
        e.stopPropagation();
        const blockId = closeButton.dataset.blockId || 'new';
        self.closeModalConfig(blockId);
        return;
      }
      
      const clickedModal = e.target.closest('.signature-modal-config');
      if (clickedModal && e.target === clickedModal) {
        e.preventDefault();
        e.stopPropagation();
        const blockId = clickedModal.dataset.blockId || clickedModal.id.replace('signature-modal-', '') || 'new';
        self.closeModalConfig(blockId);
        return;
      }
      
      const saveButton = e.target.closest('.signature-save-button-config');
      if (saveButton && (e.target === saveButton || saveButton.contains(e.target))) {
        e.preventDefault();
        e.stopPropagation();
        self.saveSignatureConfig({ currentTarget: saveButton });
        return;
      }
      
      const clearButton = e.target.closest('.signature-clear-button-config');
      if (clearButton && (e.target === clearButton || clearButton.contains(e.target))) {
        e.preventDefault();
        e.stopPropagation();
        self.clearSignature({ currentTarget: clearButton });
        return;
      }
    });
  }

  ensureJohnHancockLoaded() {
    return new Promise((resolve) => {
      if (typeof JohnHancock !== 'undefined') {
        resolve();
        return;
      }

      // Check if script is already being loaded
      if (document.querySelector('script[src*="john_hancock"]')) {
        // Wait for it to load
        const checkInterval = setInterval(() => {
          if (typeof JohnHancock !== 'undefined') {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        return;
      }

      // Load the script
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/john_hancock@0.1.0/dist/john_hancock.min.js';
      script.onload = () => resolve();
      script.onerror = () => {
        console.error('Failed to load John Hancock library');
        resolve(); // Resolve anyway to prevent blocking
      };
      document.head.appendChild(script);
    });
  }

  initializeSignaturePad() {
    if (!this.hasCanvasTarget) {
      console.error('Canvas target not found');
      // Use native canvas as fallback
      this.initializeNativeCanvas();
      return;
    }
    
    const canvas = this.canvasTarget;
    const canvasId = canvas.id;
    
    // Ensure canvas has proper dimensions
    if (!canvas.width || canvas.width === 0) {
      canvas.width = 600;
    }
    if (!canvas.height || canvas.height === 0) {
      canvas.height = 300;
    }
    
    // Check if JohnHancock is available (loaded via script tag)
    if (typeof JohnHancock === 'undefined') {
      console.warn('JohnHancock is not loaded, using native canvas drawing');
      this.initializeNativeCanvas();
      return;
    }
    
    // Clean up native canvas if it was initialized
    if (this.nativeCanvasCleanup) {
      this.nativeCanvasCleanup();
      this.nativeCanvasCleanup = null;
    }
    
    try {
      // Initialize John Hancock
      this.signaturePad = new JohnHancock(canvasId, {
        width: canvas.width,
        height: canvas.height,
        lineWidth: 2,
        lineColor: "#000000"
      });
      console.log('John Hancock initialized successfully');
    } catch (error) {
      console.error('Error initializing John Hancock:', error);
      // Fallback to native canvas drawing
      this.initializeNativeCanvas();
    }
  }

  initializeNativeCanvas() {
    if (!this.hasCanvasTarget) {
      console.error('Cannot initialize native canvas: canvas target not found');
      return;
    }
    
    this.initializeNativeCanvasForElement(this.canvasTarget, this.blockId);
  }

  initializeNativeCanvasForElement(canvas, blockId) {
    if (!canvas) {
      console.error('Cannot initialize native canvas: canvas element not provided');
      return;
    }
    
    // Clean up any existing listeners first
    if (this.nativeCanvasCleanup) {
      this.nativeCanvasCleanup();
    }
    if (this.tempCanvasCleanup) {
      this.tempCanvasCleanup();
    }
    
    // Ensure canvas has dimensions
    if (!canvas.width || canvas.width === 0) {
      canvas.width = 600;
    }
    if (!canvas.height || canvas.height === 0) {
      canvas.height = 300;
    }
    
    const ctx = canvas.getContext("2d");
    
    // Set up drawing properties
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    
    let isDrawing = false;
    let lastX = 0;
    let lastY = 0;
    
    const getCoordinates = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      
      if (e.touches && e.touches.length > 0) {
        // Touch event
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY
        };
      } else {
        // Mouse event
        return {
          x: (e.clientX - rect.left) * scaleX,
          y: (e.clientY - rect.top) * scaleY
        };
      }
    };
    
    const startDrawing = (e) => {
      e.preventDefault();
      e.stopPropagation();
      isDrawing = true;
      const coords = getCoordinates(e);
      lastX = coords.x;
      lastY = coords.y;
      
      // Draw a point at the start
      ctx.beginPath();
      ctx.arc(lastX, lastY, 1, 0, 2 * Math.PI);
      ctx.fill();
    };
    
    const draw = (e) => {
      if (!isDrawing) return;
      e.preventDefault();
      e.stopPropagation();
      
      const coords = getCoordinates(e);
      
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
      
      lastX = coords.x;
      lastY = coords.y;
    };
    
    const stopDrawing = (e) => {
      if (!isDrawing) return;
      e.preventDefault();
      e.stopPropagation();
      isDrawing = false;
    };
    
    // Mouse events
    canvas.addEventListener("mousedown", startDrawing, { passive: false });
    canvas.addEventListener("mousemove", draw, { passive: false });
    canvas.addEventListener("mouseup", stopDrawing, { passive: false });
    canvas.addEventListener("mouseout", stopDrawing, { passive: false });
    canvas.addEventListener("mouseleave", stopDrawing, { passive: false });
    
    // Touch events
    canvas.addEventListener("touchstart", startDrawing, { passive: false });
    canvas.addEventListener("touchmove", draw, { passive: false });
    canvas.addEventListener("touchend", stopDrawing, { passive: false });
    canvas.addEventListener("touchcancel", stopDrawing, { passive: false });
    
    // Store cleanup function - use different property for config mode
    const cleanup = () => {
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("mouseout", stopDrawing);
      canvas.removeEventListener("mouseleave", stopDrawing);
      canvas.removeEventListener("touchstart", startDrawing);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", stopDrawing);
      canvas.removeEventListener("touchcancel", stopDrawing);
    };
    
    if (canvas === this.canvasTarget) {
      this.nativeCanvasCleanup = cleanup;
    } else {
      this.tempCanvasCleanup = cleanup;
    }
    
    console.log('Native canvas drawing initialized successfully');
  }

  openSignatureModal() {
    if (this.readOnly) return;
    
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden");
      this.modalTarget.style.setProperty('display', 'flex', 'important');
      this.modalTarget.style.setProperty('visibility', 'visible', 'important');
      this.modalTarget.style.setProperty('opacity', '1', 'important');
      
      document.body.style.overflow = 'hidden';
      
      // Get canvas and clear it
      if (this.hasCanvasTarget) {
        const canvas = this.canvasTarget;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width || 600, canvas.height || 300);
        
        // Ensure canvas has proper dimensions
        if (!canvas.width || canvas.width === 0) {
          canvas.width = 600;
        }
        if (!canvas.height || canvas.height === 0) {
          canvas.height = 300;
        }
        
        // Clear again after setting dimensions
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      
      // Load existing signature name into modal input if it exists
      const blockId = this.blockId;
      const nameInput = this.hasNameInputTarget ? this.nameInputTarget : null;
      const modalNameInput = document.getElementById(`signature-modal-name-input-${blockId}`);
      if (nameInput && modalNameInput) {
        modalNameInput.value = nameInput.value || '';
      } else if (this.signedByName && modalNameInput) {
        modalNameInput.value = this.signedByName;
      }
      
      // Request geolocation early (when modal opens) so it's ready when user saves
      // This gives the browser more time to get the location
      this.requestGeolocationEarly();
      
      // Initialize native canvas drawing
      if (this.hasCanvasTarget) {
        setTimeout(() => {
          const canvas = this.canvasTarget;
          
          // Ensure dimensions are still set
          if (!canvas.width || canvas.width === 0) {
            canvas.width = 600;
          }
          if (!canvas.height || canvas.height === 0) {
            canvas.height = 300;
          }
          
          // Clear canvas one more time before initializing drawing
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          
          // Always initialize native canvas for reliable drawing
          if (!this.nativeCanvasCleanup) {
            this.initializeNativeCanvas();
          }
        }, 100);
      }
      
      // Optionally try to use John Hancock if available (but native canvas is primary)
      this.ensureJohnHancockLoaded().then(() => {
        if (this.hasCanvasTarget && typeof JohnHancock !== 'undefined') {
          console.log('John Hancock loaded, but using native canvas for reliability');
        }
      });
    }
  }
  
  async requestGeolocationEarly() {
    // Request geolocation as soon as modal opens to give it time to get location
    // Store it so we can use it when saving
    if (!navigator.geolocation) {
      console.warn("Geolocation API not available in this browser");
      this.pendingGeolocation = null;
      return;
    }
    
    // Check if we're in a secure context (HTTPS or localhost)
    if (!window.isSecureContext) {
      console.warn("Geolocation requires secure context (HTTPS or localhost)");
      this.pendingGeolocation = null;
      return;
    }
    
    // Check permission state if available (modern browsers)
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
        console.log("Geolocation permission state:", permissionStatus.state);
        
        if (permissionStatus.state === 'denied') {
          console.warn("Geolocation permission is denied. User needs to enable it in browser settings.");
          this.pendingGeolocation = null;
          return;
        }
        
        // Listen for permission changes
        permissionStatus.onchange = () => {
          console.log("Geolocation permission changed to:", permissionStatus.state);
          if (permissionStatus.state === 'granted') {
            // Try again if permission was just granted
            this.requestGeolocationEarly();
          }
        };
      } catch (e) {
        console.warn("Could not check geolocation permission state:", e);
        // Continue anyway - some browsers don't support permissions API
      }
    }
    
    console.log("Requesting geolocation early (when modal opens)...");
    this.pendingGeolocation = null;
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.pendingGeolocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        console.log("✅ Geolocation captured early (when modal opened):", this.pendingGeolocation);
      },
      (error) => {
        console.warn("❌ Geolocation failed when modal opened:", error);
        console.warn("Error code:", error.code);
        console.warn("Error message:", error.message);
        // Error codes: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        if (error.code === 1) {
          console.warn("⚠️ User denied geolocation permission. Please check browser settings:");
          console.warn("   - Chrome: Click lock icon → Site settings → Location → Allow");
          console.warn("   - Firefox: Click lock icon → More Information → Permissions → Location → Allow");
          console.warn("   - Safari: Safari → Settings → Websites → Location → Allow");
        } else if (error.code === 2) {
          console.warn("⚠️ Geolocation position unavailable (GPS might be off or unavailable)");
        } else if (error.code === 3) {
          console.warn("⚠️ Geolocation request timed out");
        }
        this.pendingGeolocation = null;
      },
      {
        timeout: 15000, // 15 seconds timeout
        enableHighAccuracy: false, // Changed to false - high accuracy can cause issues
        maximumAge: 60000 // Allow cached position up to 1 minute old
      }
    );
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden");
      this.modalTarget.style.setProperty('display', 'none', 'important');
      this.modalTarget.style.setProperty('visibility', 'hidden', 'important');
      this.modalTarget.style.setProperty('opacity', '0', 'important');
      document.body.style.overflow = '';
    }
  }

  openSignatureModalConfig(blockId) {
    let modal = document.getElementById(`signature-modal-${blockId}`);
    
    if (!modal) {
      const allModals = document.querySelectorAll('.signature-modal-config');
      if (allModals.length > 0) {
        for (let m of allModals) {
          const modalBlockId = m.dataset.blockId || m.id.replace('signature-modal-', '');
          if (modalBlockId === String(blockId) || modalBlockId === blockId) {
            modal = m;
            break;
          }
        }
        if (!modal && allModals.length > 0) {
          modal = allModals[0];
        }
      }
    }
    
    if (!modal) {
      console.error(`Modal not found for blockId: ${blockId}. Available modals:`, document.querySelectorAll('.signature-modal-config'));
      return;
    }
    
    modal.classList.remove("hidden");
    modal.style.setProperty('display', 'flex', 'important');
    modal.style.setProperty('visibility', 'visible', 'important');
    modal.style.setProperty('opacity', '1', 'important');
    document.body.style.overflow = 'hidden';
    
    const canvas = modal.querySelector(`#JohnHancock-canvas-config-${blockId}`);
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width || 600, canvas.height || 300);
      
      if (!canvas.width || canvas.width === 0) {
        canvas.width = 600;
      }
      if (!canvas.height || canvas.height === 0) {
        canvas.height = 300;
      }
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    
    const nameInput = document.getElementById(`signature-name-input-${blockId}`);
    const modalNameInput = document.getElementById(`signature-modal-name-input-${blockId}`);
    if (nameInput && modalNameInput) {
      modalNameInput.value = nameInput.value || '';
    }
    
    if (canvas) {
      this.tempCanvas = canvas;
      this.tempBlockId = blockId;
      
      setTimeout(() => {
        if (!canvas.width || canvas.width === 0) {
          canvas.width = 600;
        }
        if (!canvas.height || canvas.height === 0) {
          canvas.height = 300;
        }
        
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        this.initializeNativeCanvasForElement(canvas, blockId);
      }, 100);
    } else {
      console.error(`Canvas not found for blockId: ${blockId}`);
    }
  }

  closeModalConfig(blockId) {
    const modal = document.getElementById(`signature-modal-${blockId}`);
    if (modal) {
      const canvas = modal.querySelector(`#JohnHancock-canvas-config-${blockId}`);
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width || 600, canvas.height || 300);
      }
      
      modal.classList.add("hidden");
      modal.style.setProperty('display', 'none', 'important');
      modal.style.setProperty('visibility', 'hidden', 'important');
      modal.style.setProperty('opacity', '0', 'important');
      document.body.style.overflow = '';
    }
    
    if (this.tempCanvas && this.tempBlockId === blockId) {
      if (this.tempCanvasCleanup) {
        this.tempCanvasCleanup();
        this.tempCanvasCleanup = null;
      }
      this.tempCanvas = null;
      this.tempBlockId = null;
    }
  }

  clearSignature(e) {
    let canvas = null;
    let blockId = null;
    
    // First, try to use the canvas target (for task list modal)
    if (this.hasCanvasTarget) {
      canvas = this.canvasTarget;
      blockId = this.blockId;
    } else if (e && e.currentTarget) {
      // For config modal, find the canvas from the button's context
      blockId = e.currentTarget.dataset.blockId || this.blockId || 'new';
      
      // Try to find the modal - could be task list modal or config modal
      const taskListModal = this.hasModalTarget ? this.modalTarget : null;
      const configModal = document.getElementById(`signature-modal-${blockId}`);
      const modal = taskListModal || configModal;
      
      if (modal) {
        // Try config modal canvas ID first
        canvas = modal.querySelector(`#JohnHancock-canvas-config-${blockId}`);
        // If not found, try task list modal canvas ID
        if (!canvas) {
          canvas = modal.querySelector(`#JohnHancock-canvas-${blockId}`);
        }
        // If still not found, try to find any canvas in the modal
        if (!canvas) {
          canvas = modal.querySelector('canvas');
        }
      }
    }
    
    if (!canvas) {
      console.warn('Could not find canvas to clear');
      return;
    }
    
    // Clear the canvas
    const context = canvas.getContext("2d");
    if (context) {
      context.clearRect(0, 0, canvas.width || 600, canvas.height || 300);
    }
    
    // Reset John Hancock if initialized
    if (this.signaturePad && typeof this.signaturePad.clear === 'function') {
      this.signaturePad.clear();
    }
    
    // Reinitialize native canvas drawing
    if (canvas === this.canvasTarget && this.nativeCanvasCleanup) {
      // Reinitialize for task list modal
      this.initializeNativeCanvas();
    } else if (canvas !== this.canvasTarget) {
      // Reinitialize for config modal or other canvas
      if (this.tempCanvasCleanup) {
        this.tempCanvasCleanup();
        this.tempCanvasCleanup = null;
      }
      this.initializeNativeCanvasForElement(canvas, blockId);
    }
  }

  async saveSignature() {
    if (this.readOnly || !this.hasCanvasTarget) return;
    
    const canvas = this.canvasTarget;
    
    const signatureData = canvas.toDataURL("image/png");
    
    // Get name from modal input first, then fallback to regular name input
    const blockId = this.blockId;
    const modalNameInput = document.getElementById(`signature-modal-name-input-${blockId}`);
    const nameInput = this.hasNameInputTarget ? this.nameInputTarget : null;
    let name = (modalNameInput ? modalNameInput.value.trim() : '') || (nameInput ? nameInput.value.trim() : "");
    
    
    if (!name || name.trim() === '') {
      const currentUserName = this.element.dataset.currentUserName;
      if (currentUserName && currentUserName.trim() !== '') {
        name = currentUserName;
      }
    }

    // Get GEO location, IP, and timestamp
    // Start collecting metadata early (before user finishes drawing) to allow time for geolocation
    console.log("Starting metadata collection (including geolocation)...");
    const metadata = await this.collectMetadata();
    console.log("Metadata collected:", metadata);
    
    // Create UTC timestamp (same as what we'll send to server)
    const signedAtUTC = new Date().toISOString();
    const signedAtDate = new Date(signedAtUTC); // Parse the UTC ISO string to get a Date object
    
    const { accountId, projectId, taskListId, taskId } = this.element.dataset;

    fetch(`/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}/update_content`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ 
        task_block: { 
          signature_data: signatureData,
          signed_by_name: name,
          signature_metadata: metadata,
          signed_at: signedAtUTC
        },
        skip_broadcast: false // Always broadcast - the channel will skip updates for the editing user
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.closeModal();
          
          let allowRecreationValue = null;
          if (data.allow_signature_recreation !== undefined) {
            allowRecreationValue = data.allow_signature_recreation;
            this.allowRecreation = allowRecreationValue;
            this.element.dataset.signatureBlockAllowRecreation = allowRecreationValue ? "true" : "false";
            this.element.setAttribute('data-signature-block-allow-recreation', allowRecreationValue ? "true" : "false");
          } else {
            this.allowRecreation = this.element.dataset.signatureBlockAllowRecreation === "true";
            allowRecreationValue = this.allowRecreation;
          }
          
          this.updateSignatureDisplay(signatureData, name, signedAtDate, metadata, allowRecreationValue);
          
          const { accountId, projectId, taskListId, taskId, blockId } = this.element.dataset;
          if (accountId && projectId && taskListId && taskId && blockId) {
            const event = new CustomEvent('taskBlockUpdated', {
              detail: {
                block_id: blockId,
                account_id: accountId,
                project_id: projectId,
                task_list_id: taskListId,
                task_id: taskId,
                action: 'update_value'
              }
            });
            document.dispatchEvent(event);
            
            const renderUrl = `/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}/render_referencing_task_blocks`;
            
            const ensureButtonAfterRender = () => {
              setTimeout(() => {
                const blockElement = document.querySelector(`[data-block-id="${blockId}"][data-controller*="signature-block"]`);
                if (blockElement && blockElement.signatureBlockController) {
                  blockElement.signatureBlockController.updateRecreateButtonVisibility();
                  
                  const mapElement = blockElement.querySelector('.signature-location-map');
                  if (mapElement && !mapElement._leafletMap && !mapElement._leafletInitialized) {
                    const lat = parseFloat(mapElement.dataset.latitude);
                    const lng = parseFloat(mapElement.dataset.longitude);
                    if (!isNaN(lat) && !isNaN(lng)) {
                      const clickHandler = (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        blockElement.signatureBlockController.openLocationModal(e);
                      };
                      mapElement.addEventListener('click', clickHandler);
                      blockElement.signatureBlockController.initializeLeafletMap(mapElement.id, lat, lng, clickHandler);
                    }
                  }
                } else if (blockElement) {
                  setTimeout(() => {
                    if (blockElement.signatureBlockController) {
                      blockElement.signatureBlockController.updateRecreateButtonVisibility();
                      
                      const mapElement = blockElement.querySelector('.signature-location-map');
                      if (mapElement && !mapElement._leafletMap && !mapElement._leafletInitialized) {
                        const lat = parseFloat(mapElement.dataset.latitude);
                        const lng = parseFloat(mapElement.dataset.longitude);
                        if (!isNaN(lat) && !isNaN(lng)) {
                          const clickHandler = (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            blockElement.signatureBlockController.openLocationModal(e);
                          };
                          mapElement.addEventListener('click', clickHandler);
                          blockElement.signatureBlockController.initializeLeafletMap(mapElement.id, lat, lng, clickHandler);
                        }
                      }
                    }
                  }, 200);
                }
              }, 300);
            };
            
            if (typeof Rails !== 'undefined' && Rails.ajax) {
              Rails.ajax({
                url: renderUrl,
                type: 'GET',
                dataType: "script",
                success: ensureButtonAfterRender
              });
            } else {
              fetch(renderUrl, {
                method: 'GET',
                headers: {
                  'Accept': 'text/javascript',
                  'X-Requested-With': 'XMLHttpRequest',
                  'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                },
                credentials: 'same-origin'
              }).then(() => {
                ensureButtonAfterRender();
              });
            }
          }
        } else {
          console.error("Failed to save signature:", data);
          alert("Failed to save signature. Please try again.");
        }
      })
      .catch(error => {
        console.error("Error saving signature:", error);
        alert("Error saving signature. Please try again.");
      });
  }

  formatDateForUserTimezone(date) {
    // Format date according to user's timezone preference
    // Format: "16 Nov 2025 06:50 PM" (matching server format: "%d %b %Y %I:%M %p")
    // Ensure date is a Date object
    if (!(date instanceof Date)) {
      date = new Date(date);
    }
    
    // Use the timezone we determined in connect() (browser timezone if user timezone is UTC)
    let timezone = this.userTimezone;
    
    // Fallback to browser timezone if still not set
    if (!timezone || timezone === 'UTC' || timezone.trim() === '') {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.warn('Timezone still UTC/empty, using browser timezone:', timezone);
    }
    
    // Normalize timezone string - handle common variations
    // Pakistan timezone might be stored as "Asia/Karachi" or "Pakistan Standard Time" etc.
    if (timezone && typeof timezone === 'string') {
      timezone = timezone.trim();
      // Map common timezone names to IANA timezone identifiers
      const timezoneMap = {
        'Pakistan Standard Time': 'Asia/Karachi',
        'PKT': 'Asia/Karachi',
        'Pakistan': 'Asia/Karachi',
        'UTC': Intl.DateTimeFormat().resolvedOptions().timeZone // Use browser timezone if UTC
      };
      timezone = timezoneMap[timezone] || timezone;
    }
    
    console.log('Formatting date with timezone:', timezone, 'for UTC date:', date.toISOString());
    
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      
      const parts = formatter.formatToParts(date);
      const day = parts.find(p => p.type === 'day').value;
      const month = parts.find(p => p.type === 'month').value;
      const year = parts.find(p => p.type === 'year').value;
      const hour = parts.find(p => p.type === 'hour').value;
      const minute = parts.find(p => p.type === 'minute').value;
      const dayPeriod = parts.find(p => p.type === 'dayPeriod').value;
      
      const formatted = `${day} ${month} ${year} ${hour}:${minute} ${dayPeriod.toUpperCase()}`;
      console.log('Formatted date result:', formatted, 'in timezone:', timezone);
      return formatted;
    } catch (error) {
      console.warn('Error formatting date with timezone:', timezone, error);
      // Fallback to browser's local timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      
      const parts = formatter.formatToParts(date);
      const day = parts.find(p => p.type === 'day').value;
      const month = parts.find(p => p.type === 'month').value;
      const year = parts.find(p => p.type === 'year').value;
      const hour = parts.find(p => p.type === 'hour').value;
      const minute = parts.find(p => p.type === 'minute').value;
      const dayPeriod = parts.find(p => p.type === 'dayPeriod').value;
      
      return `${day} ${month} ${year} ${hour}:${minute} ${dayPeriod.toUpperCase()}`;
    }
  }

  updateSignatureDisplay(signatureData, name, signedAt, metadata = null, allowRecreationOverride = null) {
    const signatureField = this.element.querySelector('.signature-field');
    if (!signatureField) return;
    
    // Remove existing signature display if present (but preserve other blocks' maps)
    const existingDisplay = signatureField.querySelector('.signature-display');
    if (existingDisplay) {
      // Clean up this block's map before removing
      const existingMap = existingDisplay.querySelector('.signature-location-map');
      if (existingMap && existingMap._leafletMap) {
        try {
          existingMap._leafletMap.remove();
        } catch (e) {}
      }
      existingDisplay.remove();
    }
    
    const inputContainer = signatureField.querySelector('.signature-input-container');
    if (inputContainer) {
      inputContainer.remove();
    }
    
    const formattedDate = this.formatDateForUserTimezone(signedAt);
    
    
    let displayName = (name && name.trim() !== '') ? name : null;
    
    
    const signedByEmail = metadata && metadata.signed_by_email ? metadata.signed_by_email : null;
    if (displayName && signedByEmail && signedByEmail.trim() !== '') {
      displayName = `${displayName} (user: ${signedByEmail.trim()})`;
    }
    
    // Check if we have geolocation data
    const hasGeolocation = metadata && metadata.geolocation && 
                          metadata.geolocation.latitude != null && 
                          metadata.geolocation.longitude != null;
    
    const latitude = hasGeolocation ? metadata.geolocation.latitude : null;
    const longitude = hasGeolocation ? metadata.geolocation.longitude : null;
    
    // Build map HTML if geolocation exists
    let mapHTML = '';
    let mapId = null;
    if (hasGeolocation && latitude != null && longitude != null) {
      mapId = `signature-map-${this.blockId}-${Date.now()}`;
      
      mapHTML = `
        <div class="signature-location-map-container" style="flex-shrink: 0; position: relative; z-index: 1;">
          <div class="signature-location-map" 
               id="${mapId}"
               data-latitude="${latitude}"
               data-longitude="${longitude}"
               data-action="click->signature-block#openLocationModal"
               style="width: 200px; height: 200px; border: 2px solid #ddd; border-radius: 4px; cursor: pointer; overflow: hidden; background: #f5f5f5; position: relative; z-index: 1;">
            <div style="position: absolute; bottom: 0; left: 0; right: 0; background: rgba(0,0,0,0.6); color: white; padding: 5px; font-size: 11px; text-align: center; pointer-events: none; z-index: 10;">
              Click to view location
            </div>
          </div>
        </div>
      `;
    }
    
    let allowRecreation = false;
    
    if (allowRecreationOverride !== null && allowRecreationOverride !== undefined) {
      if (allowRecreationOverride === true || allowRecreationOverride === "true" || allowRecreationOverride === 1) {
        allowRecreation = true;
      } else if (allowRecreationOverride === false || allowRecreationOverride === "false" || allowRecreationOverride === 0) {
        allowRecreation = false;
      } else {
        allowRecreation = !!allowRecreationOverride;
      }
    } else {
      if (this.element.dataset.signatureBlockAllowRecreation !== undefined) {
        allowRecreation = this.element.dataset.signatureBlockAllowRecreation === "true" || 
                          this.element.dataset.signatureBlockAllowRecreation === true;
      } else {
        if (this.allowRecreation !== undefined) {
          allowRecreation = this.allowRecreation === true || this.allowRecreation === "true";
        } else {
          const allowRecreationAttr = this.element.getAttribute('data-signature-block-allow-recreation');
          allowRecreation = allowRecreationAttr === "true" || allowRecreationAttr === true;
        }
      }
    }
    const isReadOnlyAttr = this.element.getAttribute('data-signature-block-read-only') || 
                          this.element.dataset.signatureBlockReadOnly;
    const isReadOnly = isReadOnlyAttr === "true" || isReadOnlyAttr === true;
    
    const showRecreateButton = allowRecreation && !isReadOnly;
    
    let buttonColor = '#005ca5';
    const existingButton = this.element.querySelector('.button');
    if (existingButton) {
      const computedStyle = window.getComputedStyle(existingButton);
      const bgColor = computedStyle.backgroundColor;
      if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
        buttonColor = bgColor;
      }
    }
    
    let recreateButtonHTML = '';
    if (showRecreateButton) {
      let buttonText = 'Record new signature';
      const existingRecreateButton = this.element.querySelector('.signature-recreate-button');
      if (existingRecreateButton) {
        const textSpan = existingRecreateButton.querySelector('span:last-child');
        if (textSpan) {
          buttonText = textSpan.textContent.trim();
        }
      }
      
      recreateButtonHTML = `
        <div style="margin-top: 10px;">
          <button type="button" 
                  class="button signature-recreate-button"
                  data-action="click->signature-block#openSignatureModal"
                  style="padding: 8px 16px; font-size: 14px; background: ${buttonColor} !important;">
            <span class="icon icon-pencil"></span>
            <span>${buttonText}</span>
          </button>
        </div>
      `;
    }
    
    const signatureDisplayHTML = `
      <div class="signature-display" style="display: flex; gap: 15px; align-items: flex-start;">
        <div class="signature-image-wrapper">
          <div class="signature-image-container">
            <img src="${signatureData}" alt="Signature" class="signature-image" style="max-width: 300px; border: 1px solid #ddd; padding: 10px; background: white;" />
          </div>
          <div class="signature-meta" style="margin-top: 10px; font-size: 14px; color: #666;">
            ${displayName ? `<span class="signature-name" style="font-weight: bold; margin-right: 10px;">${displayName}</span>` : ''}
            <span class="signature-date">${formattedDate}</span>
          </div>
          ${recreateButtonHTML}
        </div>
        ${mapHTML}
      </div>
    `;
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = signatureDisplayHTML.trim();
    const signatureDisplay = tempDiv.firstChild;
    
    signatureField.appendChild(signatureDisplay);
    
    if (hasGeolocation && latitude != null && longitude != null && mapId) {
      const mapElement = signatureField.querySelector(`#${mapId}`);
      if (mapElement) {
        mapElement.dataset.latitude = latitude;
        mapElement.dataset.longitude = longitude;
        
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openLocationModal(e);
        };
        
        mapElement.addEventListener('click', clickHandler);
        
        setTimeout(() => {
          this.initializeLeafletMap(mapElement.id, latitude, longitude, clickHandler);
        }, 150);
      }
    }
    
    // Reinitialize ALL maps on the page to fix any that disappeared
    this.reinitializeAllMaps();
    
    this.element.dataset.signatureBlockSignatureData = signatureData;
    this.element.dataset.signatureBlockSignedByName = name || '';
    this.element.dataset.signatureBlockSignedAt = signedAt.toISOString();
    if (metadata) {
      this.element.dataset.signatureBlockMetadata = JSON.stringify(metadata);
      this.signatureMetadata = metadata;
    }
    
    this.allowRecreation = allowRecreation;
    this.element.dataset.signatureBlockAllowRecreation = allowRecreation ? "true" : "false";
    this.element.setAttribute('data-signature-block-allow-recreation', allowRecreation ? "true" : "false");
    
    this.signatureData = signatureData;
    this.signedByName = name || '';
    this.signedAt = signedAt.toISOString();
    
    this.updateRecreateButtonVisibility();
  }
  
  updateRecreateButtonVisibility() {
    const allowRecreation = this.element.dataset.signatureBlockAllowRecreation === "true";
    const isReadOnly = this.element.dataset.signatureBlockReadOnly === "true";
    const shouldShowButton = allowRecreation && !isReadOnly && this.signatureData;
    
    const signatureField = this.element.querySelector('.signature-field');
    if (!signatureField) return;
    
    const signatureDisplay = signatureField.querySelector('.signature-display');
    if (!signatureDisplay) return;
    
    const signatureImageWrapper = signatureDisplay.querySelector('.signature-image-wrapper');
    if (!signatureImageWrapper) return;
    
    const existingButton = signatureImageWrapper.querySelector('.signature-recreate-button');
    
    if (shouldShowButton && !existingButton) {
      let buttonColor = '#005ca5';
      const existingButtonOnPage = this.element.querySelector('.button');
      if (existingButtonOnPage) {
        const computedStyle = window.getComputedStyle(existingButtonOnPage);
        const bgColor = computedStyle.backgroundColor;
        if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
          buttonColor = bgColor;
        }
      }
      
      let buttonText = 'Record new signature';
      const existingRecreateButton = this.element.querySelector('.signature-recreate-button');
      if (existingRecreateButton) {
        const textSpan = existingRecreateButton.querySelector('span:last-child');
        if (textSpan) {
          buttonText = textSpan.textContent.trim();
        }
      }
      
      const buttonHTML = `
        <div style="margin-top: 10px;">
          <button type="button" 
                  class="button signature-recreate-button"
                  data-action="click->signature-block#openSignatureModal"
                  style="padding: 8px 16px; font-size: 14px; background: ${buttonColor} !important;">
            <span class="icon icon-pencil"></span>
            <span>${buttonText}</span>
          </button>
        </div>
      `;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = buttonHTML.trim();
      const buttonContainer = tempDiv.firstChild;
      signatureImageWrapper.appendChild(buttonContainer);
    } else if (!shouldShowButton && existingButton) {
      existingButton.closest('div[style*="margin-top: 10px"]')?.remove();
    }
  }
  
  reinitializeAllMaps() {
    // Find all signature location maps on the page and reinitialize them
    // Don't filter by data-initialized to catch newly created maps
    const allMaps = document.querySelectorAll('.signature-location-map');
    allMaps.forEach(mapElement => {
      const latitude = parseFloat(mapElement.dataset.latitude);
      const longitude = parseFloat(mapElement.dataset.longitude);
      
      // Only initialize if we have valid coordinates and map isn't already initialized
      if (!isNaN(latitude) && !isNaN(longitude) && !mapElement._leafletMap && !mapElement._leafletInitialized) {
        // Skip if already initializing
        if (mapElement.hasAttribute('data-initializing')) {
          return;
        }
        
        mapElement.setAttribute('data-initializing', 'true');
        
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const controllerElement = mapElement.closest('[data-controller*="signature-block"]');
          if (controllerElement && controllerElement.signatureBlockController) {
            controllerElement.signatureBlockController.openLocationModal(e);
          }
        };
        
        // Remove existing click handlers to avoid duplicates
        const newClickHandler = clickHandler;
        mapElement.removeEventListener('click', clickHandler);
        mapElement.addEventListener('click', newClickHandler);
        
        if (mapElement.id) {
          const controllerElement = mapElement.closest('[data-controller*="signature-block"]');
          if (controllerElement && controllerElement.signatureBlockController) {
            controllerElement.signatureBlockController.initializeLeafletMap(mapElement.id, latitude, longitude, newClickHandler);
          } else {
            this.initializeLeafletMap(mapElement.id, latitude, longitude, newClickHandler);
          }
        } else {
          // If no ID, generate one
          const blockId = this.blockId || this.element.dataset.blockId || 'unknown';
          const mapId = `signature-map-${blockId}-${Date.now()}`;
          mapElement.id = mapId;
          const controllerElement = mapElement.closest('[data-controller*="signature-block"]');
          if (controllerElement && controllerElement.signatureBlockController) {
            controllerElement.signatureBlockController.initializeLeafletMap(mapId, latitude, longitude, newClickHandler);
          } else {
            this.initializeLeafletMap(mapId, latitude, longitude, newClickHandler);
          }
        }
        
        mapElement.setAttribute('data-initialized', 'true');
        mapElement.removeAttribute('data-initializing');
      }
    });
  }

  async saveSignatureConfig(e) {
    if (!e || !e.currentTarget) return;
    
    const blockId = e.currentTarget.dataset.blockId || 'new';
    const modal = document.getElementById(`signature-modal-${blockId}`);
    if (!modal) return;
    
    const canvas = modal.querySelector(`#JohnHancock-canvas-config-${blockId}`);
    if (!canvas) return;
    
    const signatureData = canvas.toDataURL("image/png");
    const modalNameInput = document.getElementById(`signature-modal-name-input-${blockId}`);
    const nameInput = document.getElementById(`signature-name-input-${blockId}`);
    let name = (modalNameInput ? modalNameInput.value.trim() : '') || (nameInput ? nameInput.value.trim() : "");
    
    
    if (!name || name.trim() === '') {
      
      let currentUserName = this.element?.dataset?.currentUserName;
      if (!currentUserName || currentUserName.trim() === '') {
        
        const form = modal.closest('form');
        if (form) {
          const controllerElement = form.closest('[data-controller*="signature-block"]');
          if (controllerElement) {
            currentUserName = controllerElement.dataset.currentUserName;
          }
        }
      }
      if (currentUserName && currentUserName.trim() !== '') {
        name = currentUserName;
      }
    }
    
    const metadata = await this.collectMetadata();
    
    // Create UTC timestamp ONCE - use the same timestamp for both saving and display
    const signedAtUTC = new Date().toISOString();
    const signedAtDate = new Date(signedAtUTC);
    
    const form = modal.closest('form') || document.querySelector('form.step-content-form');
    let blockIndex = null;
    let isCreateMode = false;
    
    if (form) {
      // Check if this is a create form (form action contains 'task_blocks' without an ID, or blockId is 'new')
      const formAction = form.action || '';
      const isCreateForm = formAction.includes('/task_blocks') && !formAction.match(/\/task_blocks\/\d+/);
      const isNewBlock = blockId === 'new' || blockId === null || blockId === undefined;
      
      isCreateMode = isCreateForm || isNewBlock;
      
      if (isCreateMode) {
        // For create mode, use direct task_block[signature_data] format
        const baseName = 'task_block';
        
        let signatureDataField = form.querySelector(`input[name="${baseName}[signature_data]"]`);
        if (!signatureDataField) {
          signatureDataField = document.createElement('input');
          signatureDataField.type = 'hidden';
          signatureDataField.name = `${baseName}[signature_data]`;
          form.appendChild(signatureDataField);
        }
        signatureDataField.value = signatureData;
        
        let signedByNameField = form.querySelector(`input[name="${baseName}[signed_by_name]"]`);
        if (!signedByNameField) {
          signedByNameField = document.createElement('input');
          signedByNameField.type = 'hidden';
          signedByNameField.name = `${baseName}[signed_by_name]`;
          form.appendChild(signedByNameField);
        }
        signedByNameField.value = name;
        
        let signedAtField = form.querySelector(`input[name="${baseName}[signed_at]"]`);
        if (!signedAtField) {
          signedAtField = document.createElement('input');
          signedAtField.type = 'hidden';
          signedAtField.name = `${baseName}[signed_at]`;
          form.appendChild(signedAtField);
        }
        signedAtField.value = signedAtUTC;
        
        let metadataField = form.querySelector(`input[name="${baseName}[signature_metadata]"]`);
        if (!metadataField) {
          metadataField = document.createElement('input');
          metadataField.type = 'hidden';
          metadataField.name = `${baseName}[signature_metadata]`;
          form.appendChild(metadataField);
        }
        metadataField.value = JSON.stringify(metadata);
      } else {
        // For update mode, use nested task[task_blocks_attributes][INDEX][signature_data] format
        const allIdFields = form.querySelectorAll('input[name*="task_blocks_attributes"][name*="[id]"]');
        allIdFields.forEach((field) => {
          if (field.value === String(blockId) || field.value === blockId) {
            const nameMatch = field.name.match(/task_blocks_attributes\[(\d+)\]/);
            if (nameMatch) {
              blockIndex = nameMatch[1];
            }
          }
        });
        
        if (blockIndex === null) {
          const blockTypeFields = form.querySelectorAll('input[name*="task_blocks_attributes"][name*="block_type"]');
          blockTypeFields.forEach(field => {
            if (field.value === 'signature') {
              const nameMatch = field.name.match(/task_blocks_attributes\[(\d+)\]/);
              if (nameMatch) {
                const index = nameMatch[1];
                const idField = form.querySelector(`input[name="task[task_blocks_attributes][${index}][id]"]`);
                if (idField && (idField.value === String(blockId) || idField.value === blockId)) {
                  blockIndex = index;
                }
              }
            }
          });
        }
        
        if (blockIndex === null) {
          blockIndex = '0';
        }
        
        const baseName = `task[task_blocks_attributes][${blockIndex}]`;
        
        let signatureDataField = form.querySelector(`input[name="${baseName}[signature_data]"]`);
        if (!signatureDataField) {
          signatureDataField = document.createElement('input');
          signatureDataField.type = 'hidden';
          signatureDataField.name = `${baseName}[signature_data]`;
          form.appendChild(signatureDataField);
        }
        signatureDataField.value = signatureData;
        
        let signedByNameField = form.querySelector(`input[name="${baseName}[signed_by_name]"]`);
        if (!signedByNameField) {
          signedByNameField = document.createElement('input');
          signedByNameField.type = 'hidden';
          signedByNameField.name = `${baseName}[signed_by_name]`;
          form.appendChild(signedByNameField);
        }
        signedByNameField.value = name;
        
        let signedAtField = form.querySelector(`input[name="${baseName}[signed_at]"]`);
        if (!signedAtField) {
          signedAtField = document.createElement('input');
          signedAtField.type = 'hidden';
          signedAtField.name = `${baseName}[signed_at]`;
          form.appendChild(signedAtField);
        }
        signedAtField.value = signedAtUTC;
        
        let metadataField = form.querySelector(`input[name="${baseName}[signature_metadata]"]`);
        if (!metadataField) {
          metadataField = document.createElement('input');
          metadataField.type = 'hidden';
          metadataField.name = `${baseName}[signature_metadata]`;
          form.appendChild(metadataField);
        }
        metadataField.value = JSON.stringify(metadata);
        
        let blockIdField = form.querySelector(`input[name="${baseName}[id]"]`);
        if (!blockIdField) {
          blockIdField = document.createElement('input');
          blockIdField.type = 'hidden';
          blockIdField.name = `${baseName}[id]`;
          form.appendChild(blockIdField);
        }
        blockIdField.value = blockId;
      }
    }
    
    const nameInputField = document.getElementById(`signature-name-input-${blockId}`);
    if (nameInputField) {
      nameInputField.value = name || '';
      nameInputField.dispatchEvent(new Event('input', { bubbles: true }));
      nameInputField.dispatchEvent(new Event('change', { bubbles: true }));
      
      const form = nameInputField.closest('form');
      if (form) {
        if (blockIndex !== null) {
          const nestedField = form.querySelector(`input[name="task[task_blocks_attributes][${blockIndex}][signed_by_name]"]`);
          if (nestedField) {
            nestedField.value = name || '';
          }
        }
        
        const directField = form.querySelector('input[name="task_block[signed_by_name]"]');
        if (directField) {
          directField.value = name || '';
        }
      }
    }
    
    this.closeModalConfig(blockId);
    
    const previewContainer = form ? form.querySelector('.signature-preview-field') : null;
    if (previewContainer) {

      const buttonColor = previewContainer.dataset.buttonColor;
      const buttonLabel = previewContainer.dataset.buttonLabel;

        // Remove existing input container if present
        const inputContainer = previewContainer.querySelector('.signature-input-container');
        if (inputContainer) {
          inputContainer.style.display = 'none';
        }
        
        // Create or update preview display
        let previewDiv = previewContainer.querySelector('.signature-display');
        if (!previewDiv) {
          previewDiv = document.createElement('div');
          previewDiv.className = 'signature-display';
          previewDiv.style.cssText = 'margin-top: 10px;';
          previewContainer.appendChild(previewDiv);
        }
        
        // Format date according to user's timezone preference
        // Use the same UTC timestamp we saved to the form
        const formattedDate = this.formatDateForUserTimezone(signedAtDate);
        
        previewDiv.innerHTML = `
        <div class="signature-image-container">
          ${signatureData ? `<img src="${signatureData}" alt="Signature" class="signature-image" style="max-width: 300px; border: 1px solid #ddd; padding: 10px; background: white;" />` : '<div style="width: 300px; height: 100px; border: 1px solid #ddd; padding: 10px; background: white; display: flex; align-items: center; justify-content: center; color: #999;">No signature</div>'}
        </div>
        <div class="signature-meta" style="margin-top: 10px; font-size: 14px; color: #666;">
          ${name ? `<span class="signature-name" style="font-weight: bold; margin-right: 10px;">${name}</span>` : ''}
          <span class="signature-date">${formattedDate}</span>
        </div>
        <div style="margin-top: 10px;">
          <button type="button"
                  class="button signature-update-button-config"
                  style="${buttonColor}"
                  data-block-id="${blockId}">
            <span class="icon icon-pencil"></span>
            <span>${buttonLabel}</span>
          </button>
        </div>
      `;
      }
  }

  async collectMetadata() {
    const metadata = {
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform
    };

    
    const currentUserEmail = this.element.dataset.currentUserEmail;
    if (currentUserEmail && currentUserEmail.trim() !== '') {
      metadata.signed_by_email = currentUserEmail.trim();
    }

    // Get IP address (using a service)
    try {
      const ipResponse = await fetch("https://api.ipify.org?format=json");
      const ipData = await ipResponse.json();
      metadata.ip_address = ipData.ip;
    } catch (error) {
      console.warn("Could not fetch IP address:", error);
      metadata.ip_address = "unknown";
    }

    // Get GEO location if available
    // First, try to use the location we captured when modal opened (if available)
    if (this.pendingGeolocation) {
      metadata.geolocation = this.pendingGeolocation;
      console.log("✅ Using geolocation captured when modal opened:", metadata.geolocation);
    } else if (navigator.geolocation && window.isSecureContext) {
      // If we don't have a pending location, try to get it now
      try {
        console.log("Attempting to get geolocation now (fallback)...");
        const position = await new Promise((resolve, reject) => {
          // Request geolocation with longer timeout and better options
          navigator.geolocation.getCurrentPosition(
            resolve, 
            reject, 
            { 
              timeout: 10000, // 10 seconds timeout
              enableHighAccuracy: false, // Changed to false - high accuracy can cause issues
              maximumAge: 60000 // Allow cached position up to 1 minute old
            }
          );
        });
        metadata.geolocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        console.log("✅ Geolocation captured successfully (fallback):", metadata.geolocation);
      } catch (error) {
        console.warn("❌ Could not get geolocation (fallback):", error);
        console.warn("Geolocation error code:", error.code);
        console.warn("Geolocation error message:", error.message);
        // Error codes: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        if (error.code === 1) {
          console.warn("⚠️ User denied geolocation permission. Please check browser settings.");
        } else if (error.code === 2) {
          console.warn("⚠️ Geolocation position unavailable (GPS might be off)");
        } else if (error.code === 3) {
          console.warn("⚠️ Geolocation request timed out");
        }
        metadata.geolocation = null;
      }
    } else {
      if (!navigator.geolocation) {
        console.warn("Geolocation API not available in this browser");
      }
      if (!window.isSecureContext) {
        console.warn("Geolocation requires secure context (HTTPS or localhost)");
      }
      metadata.geolocation = null;
    }
    
    // Clear pending geolocation after using it
    this.pendingGeolocation = null;

    return metadata;
  }

  openLocationModal(e) {
    const mapElement = e.currentTarget || e.target.closest('.signature-location-map');
    const latitude = parseFloat(mapElement.dataset.latitude);
    const longitude = parseFloat(mapElement.dataset.longitude);
    
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
      alert('Invalid coordinates');
      return;
    }
    
    if (!this.hasLocationModalTarget) {
      alert('Modal not found');
      return;
    }
    
    // Show modal
    this.locationModalTarget.classList.remove("hidden");
    this.locationModalTarget.style.setProperty('display', 'flex', 'important');
    this.locationModalTarget.style.setProperty('visibility', 'visible', 'important');
    this.locationModalTarget.style.setProperty('opacity', '1', 'important');
    document.body.style.overflow = 'hidden';
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.initializeLocationMap(latitude, longitude);
      });
    });
  }
  
  closeLocationModal() {
    if (this.hasLocationModalTarget) {
      this.locationModalTarget.classList.add("hidden");
      this.locationModalTarget.style.setProperty('display', 'none', 'important');
      this.locationModalTarget.style.setProperty('visibility', 'hidden', 'important');
      this.locationModalTarget.style.setProperty('opacity', '0', 'important');
      document.body.style.overflow = '';
      
      let mapContainer = null;
      if (this.hasLocationModalTarget) {
        const blockId = this.blockId || this.locationModalTarget.dataset.blockId;
        
        if (blockId) {
          mapContainer = document.getElementById(`signature-location-map-large-${blockId}`);
        }
        
        if (!mapContainer) {
          mapContainer = this.locationModalTarget.querySelector('.signature-location-map-large') ||
                        this.locationModalTarget.querySelector('#signature-location-map-large');
        }
      }
      
      if (mapContainer && this.locationMap) {
        try {
          if (this.locationMap.remove) {
            this.locationMap.remove(); // Leaflet map cleanup
          }
        } catch (e) {
          console.warn("Error removing map:", e);
        }
        mapContainer.innerHTML = '';
        this.locationMap = null;
      }
      
      // Remove resize handler if it exists
      if (this._locationMapResizeHandler) {
        window.removeEventListener('resize', this._locationMapResizeHandler);
        this._locationMapResizeHandler = null;
      }
    }
  }
  
  initializeLeafletMap(mapId, latitude, longitude, clickHandler = null, retryCount = 0) {
    const mapElement = document.getElementById(mapId);
    if (!mapElement) {
      console.warn("Map element not found:", mapId);
      return;
    }
    
    if (mapElement._leafletInitialized || mapElement._leafletMap) {
      return;
    }
    
    if (typeof L === 'undefined') {
      if (retryCount < 50) {
        setTimeout(() => {
          this.initializeLeafletMap(mapId, latitude, longitude, clickHandler, retryCount + 1);
        }, 100);
        return;
      }
      console.error("Leaflet.js is not loaded after retries. Please ensure Leaflet is included in the page.");
      mapElement.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 12px; text-align: center; padding: 10px;">
        <div>📍<br>${latitude.toFixed(4)}, ${longitude.toFixed(4)}</div>
      </div>`;
      return;
    }
    
    try {
      mapElement._leafletInitialized = true;
      const map = L.map(mapId, {
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        touchZoom: false,
        doubleClickZoom: false,
        scrollWheelZoom: false,
        boxZoom: false,
        keyboard: false
      }).setView([latitude, longitude], 13);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap'
      }).addTo(map);
      
      L.marker([latitude, longitude], {
        icon: L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        })
      }).addTo(map);
      
      mapElement._leafletMap = map;
      
      if (clickHandler) {
        map.on('click', clickHandler);
        const mapContainer = map.getContainer();
        if (mapContainer && !mapContainer.hasAttribute('data-click-bound')) {
          mapContainer.setAttribute('data-click-bound', 'true');
          mapContainer.style.cursor = 'pointer';
        }
      }
      
      console.log("✅ Leaflet map initialized successfully for:", mapId);
    } catch (error) {
      console.error("Error initializing Leaflet map:", error);
      mapElement._leafletInitialized = false;
      mapElement.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 12px; text-align: center; padding: 10px;">
        <div>📍<br>${latitude.toFixed(4)}, ${longitude.toFixed(4)}</div>
      </div>`;
    }
  }

  initializeLocationMap(latitude, longitude) {
    // Find the map container - try multiple ways
    let mapContainer = null;
    
    if (this.hasLocationModalTarget) {
      const blockId = this.blockId || this.locationModalTarget.dataset.blockId;
      
      if (blockId) {
        mapContainer = document.getElementById(`signature-location-map-large-${blockId}`);
      }
      
      if (!mapContainer) {
        mapContainer = this.locationModalTarget.querySelector('.signature-location-map-large');
      }
      
      if (!mapContainer) {
        mapContainer = this.locationModalTarget.querySelector('#signature-location-map-large');
      }
    }
    
    if (!mapContainer) {
      mapContainer = document.getElementById('signature-location-map-large');
    }
    
    if (!mapContainer) {
      console.error('Map container not found!');
      return;
    }
    
    // Check if Leaflet is available
    if (typeof L === 'undefined') {
      mapContainer.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 500px; color: #666;">
        <div style="text-align: center;">
          <p>Leaflet.js not loaded</p>
          <p>Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}</p>
          <a href="https://www.google.com/maps?q=${latitude},${longitude}" target="_blank">Open in Google Maps</a>
        </div>
      </div>`;
      return;
    }
    
    // Clean up existing map
    if (this.locationMap) {
      try {
        this.locationMap.remove();
      } catch (e) {
        console.warn('Error removing existing map:', e);
      }
      this.locationMap = null;
    }
    
    // Clear and force dimensions aggressively
    mapContainer.innerHTML = '';
    mapContainer.style.cssText = 'width: 100% !important; height: 500px !important; min-height: 500px !important; display: block !important; visibility: visible !important; position: relative !important;';
    
    // Force parent modal to be visible if needed
    if (this.hasLocationModalTarget) {
      this.locationModalTarget.style.setProperty('display', 'flex', 'important');
      this.locationModalTarget.style.setProperty('visibility', 'visible', 'important');
      this.locationModalTarget.style.setProperty('opacity', '1', 'important');
    }
    
    // Initialize map with retry limit to prevent infinite loops
    let retryCount = 0;
    const maxRetries = 10; // Increased retries for modal visibility
    
    const initMap = () => {
      try {
        // Check if map already exists
        if (this.locationMap) {
          console.log('Map already exists, skipping initialization');
          return;
        }
        
        // Check retry limit
        if (retryCount >= maxRetries) {
          console.error('Failed to initialize map after', maxRetries, 'retries. Container dimensions:', mapContainer.offsetWidth, 'x', mapContainer.offsetHeight);
          // Try to initialize anyway if container exists
          if (mapContainer && mapContainer.parentElement) {
            try {
              console.log('Attempting final map initialization...');
              const map = L.map(mapContainer, {
                zoomControl: true,
                attributionControl: true
              }).setView([latitude, longitude], 14);
              
              L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '© OpenStreetMap'
              }).addTo(map);
              
              L.marker([latitude, longitude], {
                icon: L.icon({
                  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
                  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
                  iconSize: [25, 41],
                  iconAnchor: [12, 41],
                  popupAnchor: [1, -34],
                  shadowSize: [41, 41]
                })
              }).addTo(map).bindPopup('Signature Location');
              
              this.locationMap = map;
              
              // Force invalidate size
              setTimeout(() => {
                if (this.locationMap && this.locationMap.invalidateSize) {
                  this.locationMap.invalidateSize();
                }
              }, 100);
              console.log('✅ Map initialized successfully (final attempt)');
              return;
            } catch (e) {
              console.error('Failed to initialize map even after retries:', e);
            }
          }
          
          mapContainer.innerHTML = `<div style="padding: 20px; text-align: center; height: 500px; display: flex; align-items: center; justify-content: center;">
            <div>
              <p>Unable to load map - container not ready</p>
              <p>Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}</p>
              <a href="https://www.google.com/maps?q=${latitude},${longitude}" target="_blank" style="color: #007bff; text-decoration: underline; margin-top: 10px; display: inline-block;">Open in Google Maps</a>
            </div>
          </div>`;
          return;
        }
        
        // Check if container has dimensions - be more lenient
        const hasDimensions = mapContainer && 
                             mapContainer.offsetWidth > 0 && 
                             mapContainer.offsetHeight > 0;
        
        // Also check computed style
        const computedStyle = window.getComputedStyle(mapContainer);
        const hasComputedDimensions = computedStyle.width !== '0px' && 
                                     computedStyle.height !== '0px' &&
                                     computedStyle.display !== 'none';
        
        if (!hasDimensions && !hasComputedDimensions) {
          retryCount++;
          console.log(`Map container not ready (attempt ${retryCount}/${maxRetries}), retrying...`);
          setTimeout(initMap, 100);
          return;
        }
        
        console.log('✅ Map container ready, initializing Leaflet map...');
        
        const map = L.map(mapContainer, {
          zoomControl: true,
          attributionControl: true
        }).setView([latitude, longitude], 14);
        
        // Add tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '© OpenStreetMap'
        }).addTo(map);
        
        L.marker([latitude, longitude], {
          icon: L.icon({
            iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
          })
        }).addTo(map).bindPopup('Signature Location');
        
        this.locationMap = map;
        console.log('✅ Leaflet map initialized successfully in modal');
        
        // Invalidate size multiple times to ensure proper rendering
        setTimeout(() => {
          if (this.locationMap && this.locationMap.invalidateSize) {
            this.locationMap.invalidateSize();
          }
        }, 100);
        
        setTimeout(() => {
          if (this.locationMap && this.locationMap.invalidateSize) {
            this.locationMap.invalidateSize();
          }
        }, 300);
        
        setTimeout(() => {
          if (this.locationMap && this.locationMap.invalidateSize) {
            this.locationMap.invalidateSize();
          }
        }, 500);
        
      } catch (error) {
        console.error('Error initializing modal map:', error);
        mapContainer.innerHTML = `<div style="padding: 20px; text-align: center; height: 500px; display: flex; align-items: center; justify-content: center;">
          <div>
            <p>Error loading map: ${error.message}</p>
            <p>Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}</p>
            <a href="https://www.google.com/maps?q=${latitude},${longitude}" target="_blank" style="color: #007bff; text-decoration: underline; margin-top: 10px; display: inline-block;">Open in Google Maps</a>
          </div>
        </div>`;
      }
    };
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        initMap();
      });
    });
  }

  disconnect() {
    if (this.signaturePad) {
      this.signaturePad = null;
    }
    
    if (this.nativeCanvasCleanup) {
      this.nativeCanvasCleanup();
      this.nativeCanvasCleanup = null;
    }
    
    // Clean up all Leaflet maps
    if (this.locationMap) {
      try {
        if (this.locationMap.remove) {
          this.locationMap.remove();
        }
      } catch (e) {
        console.warn("Error removing location map:", e);
      }
      this.locationMap = null;
    }
    
    // Remove resize handler if it exists
    if (this._locationMapResizeHandler) {
      window.removeEventListener('resize', this._locationMapResizeHandler);
      this._locationMapResizeHandler = null;
    }
    
    // Clean up any thumbnail maps
    const thumbnailMaps = this.element.querySelectorAll('[id^="signature-map-"]');
    thumbnailMaps.forEach(mapEl => {
      if (mapEl._leafletMap) {
        try {
          if (mapEl._leafletMap.remove) {
            mapEl._leafletMap.remove();
          }
        } catch (e) {
          console.warn("Error removing thumbnail map:", e);
        }
        mapEl._leafletMap = null;
      }
    });
  }
}

